import axios from 'axios';

const resolveApiBaseUrl = () => {
  // In browser we always use same-origin API path to avoid mixed content.
  if (typeof window !== 'undefined') {
    return '/api';
  }

  const envUrl = (process.env.REACT_APP_API_URL || '').trim();

  // Default to same-origin API path to avoid mixed-content issues.
  if (!envUrl) {
    return '/api';
  }

  // Keep relative paths as-is.
  if (envUrl.startsWith('/')) {
    return envUrl;
  }

  return envUrl;
};

const API_BASE_URL = resolveApiBaseUrl();

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add request interceptor to include token
api.interceptors.request.use((config) => {
  // Last-resort protection if any absolute http URL slips in.
  if (typeof config.baseURL === 'string' && config.baseURL.startsWith('http://')) {
    config.baseURL = config.baseURL.replace(/^http:\/\//i, 'https://');
  }
  if (typeof config.url === 'string' && config.url.startsWith('http://')) {
    config.url = config.url.replace(/^http:\/\//i, 'https://');
  }

  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  register: (username, email, password, fullName, invitationToken) =>
    api.post('/auth/register', { username, email, password, full_name: fullName, invitation_token: invitationToken }),
  login: (username, password) =>
    api.post('/auth/login', { username, password }),
  getCurrentUser: () =>
    api.get('/auth/me'),
  updateProfile: (payload) =>
    api.patch('/auth/profile', payload),
  changePassword: (oldPassword, newPassword) =>
    api.post('/auth/change-password', { old_password: oldPassword, new_password: newPassword }),
  forgotPassword: (email) =>
    api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) =>
    api.post('/auth/reset-password', { token, new_password: newPassword }),
  validateInvitation: (token) =>
    api.get(`/auth/validate-invitation/${token}`)
};

export const searchAPI = {
  searchMedia: (query, mediaType = 'tv') =>
    api.post('/search/media', { query, media_type: mediaType }),
  getReleases: (payload, options = {}) =>
    api.post('/search/releases', payload, { signal: options.signal }),
  grabRelease: (guid, mediaType, indexerId = null) =>
    api.post('/search/grab', {
      guid,
      media_type: mediaType,
      ...(indexerId ? { indexer_id: indexerId } : {})
    }),
  getQualityProfiles: (mediaType = 'tv') =>
    api.get('/library/profiles', { params: { media_type: mediaType } }),
  addToLibrary: (payload) =>
    api.post('/library/add', payload),
  getLibrary: (mediaType = 'all', includeDetails = true) =>
    api.get('/library/list', { params: { media_type: mediaType, include_details: includeDetails } }),
  getLibraryDetail: (mediaType, id) =>
    api.get('/library/detail', { params: { media_type: mediaType, id } }),
  getRecommendationGenres: (mediaType = 'tv') =>
    api.get('/library/recommendation-genres', { params: { media_type: mediaType } }),
  getMediaDetails: (mediaType, tmdbId, options = {}) =>
    api.get(
      !tmdbId && mediaType === 'tv' && options.tvdbId
        ? `/library/media/tv/tvdb/${options.tvdbId}/details`
        : `/library/media/${mediaType}/${tmdbId}/details`,
      { signal: options.signal, timeout: !tmdbId && options.tvdbId ? 30000 : 20000 }
    ),
  getMediaImdb: (mediaType, tmdbId) =>
    api.get(`/library/media/${mediaType}/${tmdbId}/imdb`, { timeout: 15000 }),
  getRecommendations: (scope = 'user', limit = 20, includeExisting = false, mediaType = 'tv', genreId = '', sortBy = 'match', options = {}) =>
    api.post('/library/recommendations', {
      scope,
      media_type: mediaType,
      limit,
      include_existing: includeExisting,
      sort: sortBy,
      page: options.page || 1,
      ...(options.excludeTmdbIds?.length ? { exclude_tmdb_ids: options.excludeTmdbIds.join(',') } : {}),
      ...(genreId ? { genre_id: genreId } : {})
    }, { signal: options.signal, timeout: 30000 }),
  triggerMissingSearch: (mediaType, id, episodeIds = null) =>
    api.post('/library/search-missing', {
      media_type: mediaType,
      id,
      ...(episodeIds ? { episode_ids: episodeIds } : {})
    }),
  removeFromLibrary: (mediaType, id, deleteFiles = false) =>
    api.delete('/library/remove', { 
      data: { 
        media_type: mediaType,
        ...(mediaType === 'tv' ? { series_id: id } : { movie_id: id }),
        delete_files: deleteFiles
      }
    }),
  getActivity: () =>
    api.get('/library/activity', { timeout: 10000 }),
  getActivityWithOptions: (source = 'combined', includeAll = false) =>
    api.get('/library/activity', { params: { source, include_all: includeAll }, timeout: 20000 }),
  remediateActivity: (payload) =>
    api.post('/library/activity/remediate', payload, { timeout: 20000 }),
  triggerSearch: (mediaType, id) =>
    api.post('/library/search', {
      media_type: mediaType,
      ...(mediaType === 'tv' ? { series_id: id } : { movie_id: id })
    }),
  triggerSearchAll: () =>
    api.post('/library/search-all')
};

export const downloadsAPI = {
  getDownloads: () =>
    api.get('/downloads'),
  getHistory: (page = 1, pageSize = 20) =>
    api.get('/downloads/history', { params: { page, page_size: pageSize } }),
  getSystemStatus: () =>
    api.get('/downloads/status'),
  getDownloadStatus: (downloadId) =>
    api.get(`/downloads/${downloadId}`),
  addDownload: (mediaName, torrentTitle, magnetLink, mediaType, torrentUrl = null) =>
    api.post('/downloads/add', { 
      media_name: mediaName,
      torrent_title: torrentTitle,
      magnet_link: magnetLink,
      torrent_url: torrentUrl,
      media_type: mediaType
    }),
  pauseDownload: (downloadId) =>
    api.post(`/downloads/${downloadId}/pause`),
  resumeDownload: (downloadId) =>
    api.post(`/downloads/${downloadId}/resume`),
  cancelDownload: (downloadId, deleteFiles = false) =>
    api.post(`/downloads/${downloadId}/cancel`, { delete_files: deleteFiles }),
  deleteDownload: (downloadId) =>
    api.delete(`/downloads/${downloadId}`)
};

export const adminAPI = {
  getAllUsers: () =>
    api.get('/admin/users'),
  toggleUserActive: (userId) =>
    api.post(`/admin/users/${userId}/toggle-active`),
  resetUserPassword: (userId, newPassword) =>
    api.post(`/admin/users/${userId}/reset-password`, { new_password: newPassword }),
  getUsersActivity: (days = 30) =>
    api.get('/admin/users/activity', { params: { days } }),
  getStaleLibrary: (months = 6, limit = 200, includeNeverPlayed = false) =>
    api.get('/admin/library/stale', {
      params: {
        months,
        limit: Math.min(500, Math.max(1, Number(limit) || 200)),
        include_never_played: includeNeverPlayed
      },
      timeout: 30000
    }),
  deleteStaleLibraryItems: (itemIds, months = 6, includeNeverPlayed = false, dryRun = false, expectedTargets = null) =>
    api.post('/admin/library/stale/delete', {
      item_ids: itemIds,
      months,
      include_never_played: includeNeverPlayed,
      dry_run: dryRun,
      ...(expectedTargets ? { expected_targets: expectedTargets } : {})
    }),
  getAllDownloads: (userId = null) =>
    api.get('/admin/downloads/all', { params: userId ? { user_id: userId } : {} }),
  getSystemStats: () =>
    api.get('/admin/system/stats'),
  getStorageStatus: () =>
    api.get('/admin/storage', { timeout: 10000 }),
  getOverview: () =>
    api.get('/admin/overview', { timeout: 20000 }),
  createInvitation: (email, daysValid = 7, sendEmail = false, recipientName = '') =>
    api.post('/admin/invitations/create', { email, days_valid: daysValid, send_email: sendEmail, recipient_name: recipientName }),
  getInvitations: () =>
    api.get('/admin/invitations'),
  revokeInvitation: (invitationId) =>
    api.delete(`/admin/invitations/${invitationId}`),
  testMailConnection: () =>
    api.post('/admin/mail/test')
};

export default api;
